import { useMutation } from "@apollo/client/react";
import { Button, ButtonProps } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CREDENTIAL_LIST_QUERY, GET_SUSPENDED_CREDENTIALS_COUNT } from "../../../graphql/queries/credentials";
import type { SaveCredentialMutation, SaveCredentialMutationVariables } from "../../../graphql/types";
import { Credential, SaveCredentialDocument } from "../../../graphql/types";

import { WrenchIcon } from "@phosphor-icons/react/dist/csr/Wrench";
import { toast } from "react-toastify";
import { KeyEditorActionModal } from "./KeyEditorActionModal";

type UnsuspendKeyButton = {
  credential: Credential;
  urlName: string;
  buttonSize?: ButtonProps["size"];
  sx?: ButtonProps["sx"];
  onCompleted?: () => void;
};

const UnsuspendKeyButton = ({ credential, urlName, buttonSize, sx, onCompleted }: UnsuspendKeyButton) => {
  const [showUnsuspendModal, setShowUnsuspendModal] = useState(false);

  const navigate = useNavigate();

  const [saveCredential] = useMutation<SaveCredentialMutation, SaveCredentialMutationVariables>(
    SaveCredentialDocument,
    {
      onCompleted: (data) => {
        setShowUnsuspendModal(false);
        if (onCompleted) onCompleted();
        if (data.save_credential?.__typename == "SaveCredentialOk") {
          toast.success(`${credential.name} was successfully unsuspended!`);
        } else {
          toast.error("There was a problem unsuspending the key.");
        }
        navigate(`/app/${urlName}/keys`);
      },
      // Update cache so the number of keys shows in the navbar gets updated as well
      refetchQueries: [GET_SUSPENDED_CREDENTIALS_COUNT, CREDENTIAL_LIST_QUERY],
    }
  );

  const handleUnsuspend = () => {
    saveCredential({
      variables: {
        name: credential.name,
        status: "active",
      },
    });
  };

  if (credential.status !== "suspended") {
    return null;
  }

  const onClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setShowUnsuspendModal(true);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={onClick} size={buttonSize} sx={sx}>
        Unsuspend
        <WrenchIcon size={16} />
      </Button>
      <KeyEditorActionModal
        // TODO #20571 add support for "Don't show modal again" checkbox
        width="550px"
        open={showUnsuspendModal}
        onClose={() => setShowUnsuspendModal(false)}
        body="This key was suspended due to connection issues with the source. If you believe the issue has been resolved, you can unsuspend the key to resume the pipelines on their normal schedule."
        buttonAction={handleUnsuspend}
        buttonText="Confirm"
        onCancel={() => setShowUnsuspendModal(false)}
        title="Unsuspend Key?"
      />
    </>
  );
};

export { UnsuspendKeyButton };
